import { useState } from 'react';
import { Download, ArrowUpDown } from 'lucide-react';

export function QueryResultTable({ columns, rows, executionTimeMs }) {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const sortedRows = sortCol !== null
    ? [...rows].sort((a, b) => {
        const va = a[sortCol], vb = b[sortCol];
        const cmp = typeof va === 'number' ? va - vb : String(va ?? '').localeCompare(String(vb ?? ''));
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : rows;

  const toggleSort = (idx) => {
    if (sortCol === idx) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(idx); setSortDir('asc'); }
  };

  const exportCSV = () => {
    const header = columns.join(',');
    const body = sortedRows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'query-result.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const formatCell = (val, colName) => {
    if (val === null || val === undefined) return '—';
    // Format ISO timestamps as readable dates
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}(T|\s)/.test(val)) {
      const d = new Date(val);
      if (!isNaN(d)) {
        const lower = (colName || '').toLowerCase();
        // Month-level grouping: show "Apr 2025"
        if (lower.includes('month') || val.endsWith('T00:00:00') || val.endsWith('T00:00:00.000')) {
          return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        }
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
    }
    // Format date-only strings
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const d = new Date(val + 'T00:00:00');
      if (!isNaN(d)) return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    // Format numbers — detect currency-like columns
    if (typeof val === 'number' || (typeof val === 'string' && /^-?\d+(\.\d+)?$/.test(val.trim()))) {
      const num = Number(val);
      if (isNaN(num)) return String(val);
      const lower = (colName || '').toLowerCase();
      const isCurrency = lower.includes('revenue') || lower.includes('amount') || lower.includes('expense')
        || lower.includes('cost') || lower.includes('income') || lower.includes('profit')
        || lower.includes('balance') || lower.includes('total') || lower.includes('price')
        || lower.includes('payment') || lower.includes('sales');
      if (isCurrency) {
        return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    }
    return String(val);
  };

  return (
    <div className="border border-zinc-200 rounded-lg overflow-hidden mt-2">
      <div className="flex justify-between items-center px-3 py-1.5 bg-zinc-50 text-xs text-zinc-500">
        <span>{rows.length} rows{executionTimeMs ? ` · ${executionTimeMs}ms` : ''}</span>
        <button onClick={exportCSV} className="flex items-center gap-1 hover:text-zinc-800">
          <Download className="h-3 w-3" /> CSV
        </button>
      </div>
      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 sticky top-0">
            <tr>
              {columns.map((col, i) => (
                <th key={i} onClick={() => toggleSort(i)}
                    className="px-3 py-2 text-left text-zinc-700 font-semibold cursor-pointer hover:text-zinc-900 whitespace-nowrap border-b border-zinc-200">
                  {col} <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, i) => (
              <tr key={i} className="border-t border-zinc-100 hover:bg-zinc-50">
                {row.map((cell, j) => (
                  <td key={j} className="px-3 py-1.5 text-zinc-700 whitespace-nowrap">{formatCell(cell, columns[j])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
